import { Box, Button, Checkbox, Heading, RadioGroup } from '@wix/design-system'
import { useContent, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import { tryCatch } from '@wix/funnel-utils'
import { ArrowRight } from '@wix/wix-ui-icons-common'
import React, { FC, useState } from 'react'

import { markAsWixStudioAccount } from '../../../api/StudioWorkspaceService/markAsWixStudioAccount'
import { usePageLogger } from '../../../bi/mfi'
import { DEVELOPER_OPTION_KEYS, useStorageContext } from '../../../context/StorageContext/StorageContext'
import { WixStudioTemplate } from '../../reusable/WixStudioTemplate/WixStudioTemplate'
import type { Content } from './Content'
import { SidePanel } from './SidePanel'
import { wixStudioInputGroupDataHooks as dataHooks } from './WixStudioInputGroup.dataHooks'

export const WixStudioInputGroup: FC = () => {
  const [value, setValue] = useState<string[]>([])
  const [isPageLoading] = usePageLoading()
  const next = useNext()
  const { content } = useContent<Content>()
  const { question } = content
  const pageLogger = usePageLogger()
  const { save } = useStorageContext()

  const handleClick = async () => {
    if (value.some(v => v.includes('enterprise'))) {
      // answer must be tracked before markAsWixStudioAccount — KPI measures how many users who answer the design split are subsequently marked as Studio
      await tryCatch(() => pageLogger.answer({ answer: value || '' }))
      await markAsWixStudioAccount()
    } else {
      void pageLogger.answer({ answer: value || '' })
    }

    const isDeveloper: boolean = Boolean(DEVELOPER_OPTION_KEYS.filter(key => value.includes(key)).length)
    save({ isDeveloper })
    next({ [question.questionKey]: { values: value } })
  }

  const onRadioClick = (val: string) => {
    void pageLogger.clickAnswer({ buttonName: val, questionKey: question.questionKey })
    setValue([val])
  }

  const onCheckboxClick = (val: string) => {
    void pageLogger.clickAnswer({ buttonName: val, questionKey: question.questionKey })
    setValue(value.includes(val) ? value.filter(v => v !== val) : [...value, val])
  }

  return (
    <WixStudioTemplate
      backButton={content.backButton}
      isPageLoading={isPageLoading}
      pageLogger={pageLogger}
      title={content.title}
      subTitleBelow={content.subtitle}
      sidePanel={<SidePanel image={content.img} text={content.imageDescription} />}
    >
      <Box maxWidth="288px" direction="vertical" gap="SP1" marginBottom="SP7" marginTop="SP5">
        {question.content.label && (
          <Box direction="vertical" marginBottom="SP2">
            <Heading dataHook={dataHooks.inputGroupLabel()} size="small">
              {question.content.label}
            </Heading>
          </Box>
        )}
        {question.content.type === 'radio' && (
          <RadioGroup
            value={value[0]}
            selectionArea="always"
            selectionAreaSkin="outlined"
            selectionAreaPadding="12px 18px"
            onChange={key => onRadioClick(key as string)}
          >
            {question.options.map(option => (
              <RadioGroup.Radio
                key={option.optionKey}
                dataHook={dataHooks.option({ id: option.optionKey })}
                value={option.optionKey}
                checked={value[0] === option.optionKey}
              >
                {option.label}
              </RadioGroup.Radio>
            ))}
          </RadioGroup>
        )}
        {question.content.type === 'checkbox' &&
          question.options.map(option => (
            <Checkbox
              key={option.optionKey}
              selectionArea="always"
              selectionAreaSkin="outlined"
              selectionAreaPadding="12px 18px"
              onChange={() => onCheckboxClick(option.optionKey)}
              checked={value.includes(option.optionKey)}
              dataHook={dataHooks.option({ id: option.optionKey })}
            >
              {option.label}
            </Checkbox>
          ))}
      </Box>

      {content.nextButton && (
        <Box>
          <Button
            disabled={!value.length}
            suffixIcon={(content.nextButtonHasArrow && <ArrowRight />) || undefined}
            onClick={handleClick}
            dataHook={dataHooks.nextButton()}
          >
            {content.nextButton}
          </Button>
        </Box>
      )}
    </WixStudioTemplate>
  )
}
